import type { Alert } from "../shared/alerts.js";
import type { CpuInfo, Job, LinkInfo, LiveEvent, LogEntry, PowerInfo, Settings, Snapshot } from "../shared/api.js";
import type { PatternsStatus } from "../shared/patterns.js";
import type { PowerStatus } from "../shared/power.js";

export type Topic = "snapshot" | "link" | "cpus" | "jobs" | "power" | "log" | "stock" | "rules" | "groups" | "settings" | "craftables" | "alerts" | "socket" | "patterns" | "network" | "tracked" | "favorites" | "machines" | "storage";

/**
 * Holds the live state pushed over the WebSocket and tells pages what changed.
 * Reconnects with back-off and reloads the full snapshot each time. While the
 * WebSocket is down (for example a proxy that drops the Upgrade header), it
 * polls the snapshot over plain HTTP so the dashboard still shows the game state.
 */
export class LiveStore {
  snapshot: Snapshot | null = null;
  jobs = new Map<string, Job>();
  socketOpen = false;
  /** True once the WebSocket has failed and the snapshot is being polled instead. */
  polling = false;
  /** Stock changes since the page last asked, for pages that patch rows in place. */
  readonly stock = new Map<string, number>();
  lastLog: LogEntry | null = null;
  /** Keys of the favorite stacks, pinned to the top of every list. */
  favorites = new Set<string>();
  /** Last pattern status pushed by the server (pages load it themselves first). */
  patterns: PatternsStatus | null = null;
  /** Last power status pushed by the server (the Power page loads it itself first). */
  machines: PowerStatus | null = null;

  private readonly listeners = new Map<Topic, Set<() => void>>();
  private socket: WebSocket | null = null;
  private retry = 1000;
  private stopped = false;
  private pollTimer: number | undefined;

  constructor(private readonly loadSnapshot: () => Promise<Snapshot>) {}

  get link(): LinkInfo | null {
    return this.snapshot?.link ?? null;
  }
  get cpus(): CpuInfo[] {
    return this.snapshot?.cpus ?? [];
  }
  get power(): PowerInfo | null {
    return this.snapshot?.power ?? null;
  }
  get settings(): Settings | null {
    return this.snapshot?.settings ?? null;
  }
  /** Alerts not dismissed yet, newest first. */
  get alerts(): Alert[] {
    return this.snapshot?.alerts ?? [];
  }

  on(topic: Topic, fn: () => void): () => void {
    let set = this.listeners.get(topic);
    if (!set) this.listeners.set(topic, (set = new Set()));
    set.add(fn);
    return () => set!.delete(fn);
  }

  private emit(topic: Topic): void {
    for (const fn of this.listeners.get(topic) ?? []) fn();
  }

  connect(): void {
    this.stopped = false;
    if (this.pollTimer === undefined) {
      void this.poll();
      this.pollTimer = window.setInterval(() => {
        if (!this.socketOpen) void this.poll();
      }, 5000);
    }
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${proto}//${location.host}/api/ws`);
    this.socket = socket;
    socket.onopen = () => {
      this.retry = 1000;
      this.socketOpen = true;
      this.polling = false;
      this.emit("socket");
    };
    socket.onmessage = (ev) => this.onEvent(JSON.parse(ev.data) as LiveEvent);
    socket.onclose = () => {
      this.socketOpen = false;
      if (this.stopped) return;
      this.polling = true;
      this.emit("socket");
      setTimeout(() => this.connect(), this.retry);
      this.retry = Math.min(this.retry * 2, 15000);
    };
  }

  disconnect(): void {
    this.stopped = true;
    window.clearInterval(this.pollTimer);
    this.pollTimer = undefined;
    this.socket?.close();
  }

  private async poll(): Promise<void> {
    try {
      const data = await this.loadSnapshot();
      if (!this.socketOpen && !this.stopped) this.onEvent({ t: "snapshot", data });
    } catch {
      // A 401 is handled by the Api (back to login); other errors retry on the next tick.
    }
  }

  private onEvent(event: LiveEvent): void {
    switch (event.t) {
      case "snapshot":
        this.snapshot = event.data;
        this.jobs = new Map(event.data.jobs.map((j) => [j.id, j]));
        this.setFavorites(event.data.favorites ?? []);
        this.emit("snapshot");
        this.emit("link");
        this.emit("cpus");
        this.emit("jobs");
        this.emit("power");
        this.emit("settings");
        this.emit("alerts");
        break;
      case "link":
        if (this.snapshot) this.snapshot.link = event.data;
        this.emit("link");
        break;
      case "cpus":
        if (this.snapshot) this.snapshot.cpus = event.data;
        this.emit("cpus");
        break;
      case "job":
        this.jobs.set(event.data.id, event.data);
        if (this.jobs.size > 300) {
          const oldest = [...this.jobs.values()].sort((a, b) => a.updatedAt - b.updatedAt)[0];
          this.jobs.delete(oldest.id);
        }
        this.emit("jobs");
        break;
      case "power":
        if (this.snapshot) this.snapshot.power = event.data;
        this.emit("power");
        break;
      case "log":
        this.lastLog = event.data;
        this.emit("log");
        break;
      case "stock":
        for (const [key, amount] of event.data) this.stock.set(key, amount);
        this.emit("stock");
        break;
      case "rules":
        if (this.snapshot) this.snapshot.ruleCount = event.data.count;
        this.emit("rules");
        break;
      case "groups":
        this.emit("groups");
        break;
      case "settings":
        if (this.snapshot) this.snapshot.settings = event.data;
        this.emit("settings");
        break;
      case "craftables":
        if (this.snapshot) {
          this.snapshot.craftablesCount = event.data.count;
          this.snapshot.craftablesWithOre = event.data.withOre;
          this.snapshot.craftablesAt = event.data.at;
        }
        this.emit("craftables");
        break;
      case "patterns":
        this.patterns = event.data;
        this.emit("patterns");
        break;
      case "network":
        if (this.snapshot) this.snapshot.network = event.data;
        this.emit("network");
        break;
      case "tracked":
        this.emit("tracked");
        break;
      case "storage":
        this.emit("storage");
        break;
      case "favorites":
        this.setFavorites(event.data.keys);
        break;
      case "machines":
        this.machines = event.data;
        this.emit("machines");
        break;
      case "craftables.progress":
        if (this.snapshot) this.snapshot.craftablesLoading = event.data;
        this.emit("craftables");
        break;
      case "alert":
        if (this.snapshot) {
          const rest = (this.snapshot.alerts ?? []).filter((a) => a.id !== event.data.id);
          this.snapshot.alerts = [event.data, ...rest].sort((a, b) => b.lastAt - a.lastAt || b.id - a.id);
        }
        this.emit("alerts");
        break;
      case "alerts.dismissed":
        if (this.snapshot) {
          const gone = new Set(event.data.ids);
          this.snapshot.alerts = (this.snapshot.alerts ?? []).filter((a) => !gone.has(a.id));
        }
        this.emit("alerts");
        break;
    }
  }

  /** Replaces the favorites, telling pages only when they changed (snapshots are polled every 5 s without a socket). */
  setFavorites(keys: string[]): void {
    const next = new Set(keys);
    if (next.size === this.favorites.size && keys.every((k) => this.favorites.has(k))) return;
    this.favorites = next;
    this.emit("favorites");
  }

  /** Drops dismissed alerts right away, without waiting for the server's event (or the next poll). */
  dropAlerts(ids: number[]): void {
    if (!this.snapshot || !ids.length) return;
    const gone = new Set(ids);
    this.snapshot.alerts = this.snapshot.alerts.filter((a) => !gone.has(a.id));
    this.emit("alerts");
  }

  jobList(): Job[] {
    return [...this.jobs.values()].sort((a, b) => b.updatedAt - a.updatedAt);
  }
}
